import moment from 'moment';
import { buildGoogleMapsAddress } from 'utils';
import { virtualTourUrlBuilder } from '../../helpers';

angular
  .module('bb.listing_view')
  .factory('ListingViewService', ListingViewService);

ListingViewService.$inject = ['$resource', '$sce'];
function ListingViewService($resource, $sce) {
  const ListingImages = $resource('/listing/:listingId/file', {
    listingId: '@listingId',
  });

  const Favorite = $resource('/favorite/:listingId', {
    listingId: '@listingId',
  });

  const Listing = $resource(
    '/listing/:listingId',
    { listingId: '@listingId' },
    {
      delete: { method: 'POST', url: '/listing/delete/:listingId' },
    },
  );

  const ListingNeighbor = $resource('/listing/neighbor/:index', {
    index: '@index',
  });

  const OfferDetails = $resource('/listing/:listingId/offer_details', {
    listingId: '@listingId',
  });

  const Distribution = $resource('/listing/:id/distribute', { id: '@id' });

  const VirtualTourConfig = $resource('/virtual_tour/config/:listingId/', {
    listingId: '@listingId',
  });

  const ListingAnsweringSubscription = $resource(
    '/answering_subscription/find_listing_active/:listingId',
    {
      listingId: '@listingId',
    },
  );

  const CoopAgent = $resource('/listing/:listingId/is_coop_agent', {
    listingId: '@listingId',
  });

  const ListingInternalView = $resource('/listing/:listingId/view_internal', {
    listingId: '@listingId',
  });

  function formatHistory(history) {
    history.details = $sce.trustAsHtml(history.details);
  }

  function initListing($scope) {
    if (
      $scope.listing.reps &&
      $scope.listing.reps[0] &&
      $scope.listing.reps[0].location &&
      $scope.listing.reps[0].address
    ) {
      $scope.address =
        $scope.listing.org.addresses[$scope.listing.reps[0].location][
          $scope.listing.reps[0].address
        ];
    }

    if ($scope.listing.expiry) {
      $scope.listing.expiry = moment($scope.listing.expiry).format(
        'MMM DD, YYYY',
      );
    }
    if ($scope.listing.coming_soon_date) {
      $scope.listing.coming_soon_date = moment(
        $scope.listing.coming_soon_date,
      ).format('MMM DD, YYYY');
    }

    if ($scope.listing.availability_history) {
      $scope.listing.availability_history.forEach(history => {
        formatHistory(history);
      });
    }
  }

  function setDistribution(listingId, visibility, pageStatus) {
    const distribution = new Distribution({
      id: listingId,
      visibility,
      pageStatus,
    });
    return distribution.$save();
  }

  function getListingFiles(listingId) {
    return ListingImages.query({ listingId }).$promise;
  }

  function isFavorite(listingId) {
    return Favorite.get({ listingId }).$promise;
  }

  function toggleFavorite(listingId) {
    const favorite = new Favorite({ listingId });
    return favorite.$save();
  }

  function getListing(listingId) {
    return Listing.get({ listingId }).$promise;
  }

  function getNeighborListings(index, query) {
    return ListingNeighbor.get({ index, ...query }).$promise;
  }

  function deleteListing(listingId) {
    return Listing.delete({ listingId }).$promise;
  }

  function getVirtualTourConfig(listingId) {
    return VirtualTourConfig.get({ listingId }).$promise;
  }

  function getListingInternalView(listingId) {
    return ListingInternalView.get({ listingId }).$promise;
  }

  const OfferCount = $resource('/listing/:listingId/offer_count', {
    listingId: '@listingId',
  });

  const AppointmentCount = $resource('/listing/:listingId/appointments/count', {
    listingId: '@listingId',
  });

  const TeamContact = $resource('/team/:teamId', {
    teamId: '@teamId',
  });

  const OnboardingPortalAssociation = $resource(
    '/onboarding-portal/:portalId/onboarding-portal-association',
    {
      portalId: '@portalId',
    },
  );

  // move this to the offer count service when we have one
  function getOfferCount(listingId) {
    return OfferCount.get({ listingId }).$promise;
  }

  function getAppointmentCount(listingId) {
    return AppointmentCount.get({ listingId }).$promise;
  }

  function getTeamContact(teamId) {
    if (!teamId) return null;
    return TeamContact.get({ teamId }).$promise;
  }

  function generateAssets(listing, images, virtualTourConfig) {
    const assets = images.map(url => ({
      type: 'image',
      url,
    }));
    const firstImage = assets.shift();

    if (
      virtualTourConfig &&
      virtualTourConfig.virtualTourProvider &&
      virtualTourConfig.virtualTourIdentifier
    ) {
      assets.unshift({
        type: 'virtualTour',
        url: $sce.trustAsResourceUrl(
          virtualTourUrlBuilder({
            type: virtualTourConfig.virtualTourProvider,
            identifier: virtualTourConfig.virtualTourIdentifier,
            queryString: true,
          }),
        ),
      });
    }

    if (listing.neighborhood !== 'Pending') {
      const address = buildGoogleMapsAddress(listing);
      assets.unshift({
        type: 'map',
        address,
        latlng: {
          lat: listing.coords?.coordinates[1],
          lng: listing.coords?.coordinates[0],
        },
      });
    }

    if (firstImage) assets.unshift(firstImage);
    return assets;
  }

  function getOfferDetails(listingId) {
    return OfferDetails.get({ listingId }).$promise;
  }

  function getListingAnsweringSubscription(listingId) {
    return ListingAnsweringSubscription.get({ listingId }).$promise;
  }

  function isCoopAgent(listingId) {
    return CoopAgent.get({ listingId }).$promise;
  }

  function getOnboardingPortalAssociation({
    portalId,
    orgId,
    dashboardId,
    retsId,
  }) {
    return OnboardingPortalAssociation.get({
      portalId,
      orgId,
      dashboardId,
      retsId,
    }).$promise;
  }

  return {
    initListing,
    formatHistory,
    setDistribution,
    getListingFiles,
    isFavorite,
    toggleFavorite,
    getListing,
    getNeighborListings,
    deleteListing,
    getVirtualTourConfig,
    getOfferCount,
    getAppointmentCount,
    getTeamContact,
    generateAssets,
    getOfferDetails,
    getListingAnsweringSubscription,
    getOnboardingPortalAssociation,
    isCoopAgent,
    getListingInternalView,
  };
}
